/**
 * DOK Auto-Tagger — Heuristic DOK level detection for questions.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §9 Fase 3 — Auto-tags existing seeds with
 * best-guess DOK levels based on question characteristics. Results should
 * be reviewed by content authors via the governance admin UI.
 *
 * DOK Levels (Webb's Depth of Knowledge, adapted for Dutch primary):
 * 1 = Recall/Reproduce — direct fact retrieval, simple computation
 * 2 = Skill/Concept — apply a learned procedure, compare, classify
 * 3 = Strategic Thinking — multi-step reasoning, justify, plan
 * 4 = Extended Thinking — investigate, create, synthesize
 *
 * This is a HEURISTIC — it will be wrong for some questions.
 * The governance pipeline flags auto-tagged DOK for human review.
 */

import type { Question } from '../../content';
import type { DOKLevel } from '../../content/types';
export interface DokEstimate {
  dok: DOKLevel;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

// ─── Signal detectors ─────────────────────────────────────────────────────────

/** Keywords that suggest higher-order thinking (Dutch) */
const STRATEGIC_KEYWORDS = ['strategie', 'waarom', 'verklaar', 'beredeneer', 'bewijs', 'analyseer', 'vergelijk en verklaar', 'welke aanpak', 'meerdere manieren', 'meerstaps', 'combineer', 'ontwerp', 'bedenk', 'onderzoek', 'evalueer', 'beoordeel'];
const RECALL_KEYWORDS = ['wat is', 'hoeveel is', 'noem', 'welk', 'hoe heet', 'waar ligt', 'reken uit', 'bereken'];
const CONCEPT_KEYWORDS = ['vergelijk', 'sorteer', 'classificeer', 'welke regel', 'pas toe', 'los op', 'vul aan', 'welke breuk', 'zet om', 'rond af'];
function hasKeywords(text: string, keywords: string[]): number {
  const lower = text.toLowerCase();
  return keywords.filter(kw => lower.includes(kw)).length;
}

// ─── Question type signals ────────────────────────────────────────────────────

/** Question types that are inherently DOK 1 (recall) */
const DOK1_TYPES = new Set(['true_false_series', 'clock' // reading a clock = recall
]);

/** Question types that are inherently DOK 2+ (application) */
const DOK2_TYPES = new Set(['categorize', 'ordering', 'matching', 'error_spotting', 'sentence_parser', 'comparison_judge', 'verb_conjugation_flow']);

/** Question types that suggest DOK 3 (strategic) */
const DOK3_TYPES = new Set(['step_solver']);

// ─── Main estimator ──────────────────────────────────────────────────────────

/**
 * Estimate the DOK level of a question based on heuristics.
 *
 * Factors considered:
 * 1. Question type (some types are inherently higher/lower DOK)
 * 2. Keywords in question text (strategic vs. recall language)
 * 3. Tier level (higher tiers tend to require higher DOK)
 * 4. Number of steps/complexity signals (context, multi-part)
 * 5. Presence of context paragraph (suggests application)
 */
export function estimateDok(question: Question): DokEstimate {
  const text = question.question ?? '';
  const context = question.context ?? '';
  const tier = question.tier ?? 1;
  const type = question.questionType;
  let dokScore = 0;
  const reasons: string[] = [];

  // 1. Question type baseline
  if (DOK3_TYPES.has(type)) {
    dokScore += 3;
    reasons.push(`type "${type}" suggests strategic thinking`);
  } else if (DOK2_TYPES.has(type)) {
    dokScore += 2;
    reasons.push(`type "${type}" requires concept application`);
  } else if (DOK1_TYPES.has(type)) {
    dokScore += 1;
    reasons.push(`type "${type}" is typically recall`);
  } else {
    dokScore += 1.5; // neutral baseline
  }

  // 2. Keyword analysis
  const strategicHits = hasKeywords(text + ' ' + context, STRATEGIC_KEYWORDS);
  const recallHits = hasKeywords(text, RECALL_KEYWORDS);
  const conceptHits = hasKeywords(text + ' ' + context, CONCEPT_KEYWORDS);
  if (strategicHits >= 2) {
    dokScore += 1.5;
    reasons.push(`${strategicHits} strategic keywords`);
  } else if (strategicHits === 1) {
    dokScore += 0.75;
    reasons.push('1 strategic keyword');
  }
  if (conceptHits >= 2) {
    dokScore += 0.5;
    reasons.push(`${conceptHits} concept keywords`);
  }
  if (recallHits >= 2 && strategicHits === 0) {
    dokScore -= 0.5;
    reasons.push(`${recallHits} recall keywords, no strategic`);
  }

  // 3. Context paragraph suggests application (DOK 2+)
  if (context && context.length > 50) {
    dokScore += 0.5;
    reasons.push('has context paragraph (application)');
  }

  // 4. Tier influence (gentle — tier is difficulty, not DOK, but correlated)
  if (tier >= 13) {
    dokScore += 0.5;
    reasons.push('expert tier (13+)');
  } else if (tier >= 9) {
    dokScore += 0.25;
    reasons.push('advanced tier (9-12)');
  }

  // 5. Multi-step signals
  if (text.includes('stap') || text.includes('eerst') || text.includes('daarna')) {
    dokScore += 0.5;
    reasons.push('multi-step language detected');
  }

  // Convert score to DOK level
  let dok: DOKLevel;
  if (dokScore >= 3.5) {
    dok = 4;
  } else if (dokScore >= 2.5) {
    dok = 3;
  } else if (dokScore >= 1.5) {
    dok = 2;
  } else {
    dok = 1;
  }

  // Confidence based on signal strength
  const signalCount = reasons.length;
  let confidence: 'high' | 'medium' | 'low';
  if (signalCount >= 3 && (strategicHits >= 2 || DOK3_TYPES.has(type))) {
    confidence = 'high';
  } else if (signalCount >= 2) {
    confidence = 'medium';
  } else {
    confidence = 'low';
  }
  return {
    dok,
    confidence,
    reason: reasons.join('; ')
  };
}

/**
 * Batch-estimate DOK for an array of questions.
 * Returns a map of questionId → DokEstimate.
 */
export function batchEstimateDok(questions: Question[]): Map<string, DokEstimate> {
  const results = new Map<string, DokEstimate>();
  for (const q of questions) {
    // Skip questions that already have DOK set
    if (q.pedagogy?.dok !== undefined) continue;
    results.set(q.id, estimateDok(q));
  }
  return results;
}

/**
 * Generate a summary report of DOK estimates for governance review.
 */
export function generateDokReport(estimates: Map<string, DokEstimate>): {
  byDok: Record<number, number>;
  byConfidence: Record<string, number>;
  total: number;
} {
  const byDok: Record<number, number> = {
    1: 0,
    2: 0,
    3: 0,
    4: 0
  };
  const byConfidence: Record<string, number> = {
    high: 0,
    medium: 0,
    low: 0
  };
  for (const est of estimates.values()) {
    byDok[est.dok] = (byDok[est.dok] ?? 0) + 1;
    byConfidence[est.confidence] = (byConfidence[est.confidence] ?? 0) + 1;
  }
  return {
    byDok,
    byConfidence,
    total: estimates.size
  };
}